import { useEffect, type ReactNode } from "react";
import { Redirect, Route, useLocation, useParams } from "wouter";

import { Guarded } from "@/app/guards";
import {
  AlertsPage, AuditGroupsPage, AuditPage, AuthRequestApprovalPage, AuthRequestListPage, ChangelogPage, ConnectionsPage,
  Dashboard, DealDetailPage, DealsPage, GuardianReview, GuardianSetup, GuardianStepUpPage, InvitationsPage,
  GuardianAccountPage, MemoryVault, OrgVaultPage, OrgVaultPayoutApprovePage, OrgVaultPayoutRequestPage, OrgVaultPayoutsPage,
  RecoveryApprovalPage, SecurityCentre, SecurityKeys, SecurityRecovery, SettingsPage, VaultIdentifiers,
} from "@/app/lazy-pages";
import { AppShellLayout } from "@/components/layout/app-shell";
import { GuardianShell } from "@/components/layout/guardian-shell";
import { SecuritySectionTabs } from "@/components/layout/security-section-tabs";
import { guardianLanding } from "@/lib/guardian-only-routing";
import { useOrgContext } from "@/lib/use-org-context";
import type { OrgTabId } from "@shared/dashboard-types";

export interface CurrentUser {
  id: string;
  username: string;
  displayName: string;
  role: string;
  aal: "AAL1" | "AAL2" | "AAL3";
  emailVerified: boolean;
  mfaEnabled: boolean;
  isCompanyVerified: boolean;
  orgId?: string;
  safetyPhrase?: string | null;
  commsReadReceiptsOptIn?: boolean;
  /**
   * Guardian-only containment (ledger #16). DISPLAY HINT ONLY — the real
   * boundary is `server/middleware/guardian-only-allowlist.ts`. Used to
   * suppress surfaces that would otherwise render a permanent error state off
   * a 403 the allowlist issues by design.
   */
  guardianOnly?: boolean;
  /**
   * Ways-back-in ladder (#2210 module 06, AC-10). Self-scoped, display-only —
   * identifies which credential authenticated THIS session so the client can
   * exclude it when counting "other" active credentials. Never security-
   * load-bearing: no gate anywhere may read this field.
   */
  sessionCredentialId?: string | null;
  /**
   * Guardian-initiated recovery interrupt (Task 5b, AC-12, ruling
   * S7-M03-113). SELF-SCOPED, DISPLAY hint only — the guard in
   * `server/middleware/shield/recovery-claim-guard.ts` is the enforcement,
   * never this field. When set, the client's post-auth redirect resolution
   * routes here BEFORE returnTo/guardian-landing (the interrupt wins).
   */
  pendingRecoveryClaim?: string | null;
  /**
   * Memory-capture-flow session sheets (AC-17). SELF-SCOPED timing only: epoch-
   * ms of this session's creation / last server-seen activity. DISPLAY hint —
   * the 30-minute cap and 10-minute idle timeout are enforced by `requireAuth`,
   * never by these. Absent/null on a server that predates the fields.
   */
  sessionCreatedAtMs?: number | null;
  sessionLastActivityMs?: number | null;
}

interface AppRouteContext {
  user: CurrentUser | null;
  loading: boolean;
  resolveDashboardTabRedirect: (tab: string | undefined, search?: string) => string | null;
  validOrgTab: (tab: string | undefined) => OrgTabId;
}

function appShellPage(context: AppRouteContext, page: ReactNode, minAal: "AAL1" | "AAL2" | "AAL3" = "AAL2"): JSX.Element {
  return <Guarded user={context.user} loading={context.loading} minAal={minAal}>
    <AppShellLayout displayName={context.user?.displayName} aal={context.user?.aal}>{page}</AppShellLayout>
  </Guarded>;
}

function guardianShellPage(context: AppRouteContext): JSX.Element {
  if (context.user && !context.user.guardianOnly) {
    return <Redirect to={guardianLanding(context.user)} />;
  }
  return <Guarded user={context.user} loading={context.loading}>
    <GuardianShell displayName={context.user?.displayName}><GuardianAccountPage /></GuardianShell>
  </Guarded>;
}

function DashboardRoute({ context, initialTab }: { context: AppRouteContext; initialTab: OrgTabId }): JSX.Element {
  const { currentContext } = useOrgContext();
  if (currentContext === "personal") return <Redirect to="/security-centre" />;
  return appShellPage(context, <Dashboard initialTab={initialTab} />);
}

/** Redirect legacy identifier-history routes into the canonical filtered audit view. */
export function VaultIdentifierHistoryRedirect(): null {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation(`/audit?identifier=${encodeURIComponent(params.id ?? "")}`, { replace: true });
  }, [params.id, setLocation]);
  return null;
}

/** Build persistent-shell application routes as direct children for Wouter's outer Switch. */
export function buildAppRoutes(context: AppRouteContext): JSX.Element {
  return <>
    <Route path="/guardian">{guardianShellPage(context)}</Route>
    <Route path="/guardian-setup">{appShellPage(context, <SecuritySectionTabs><GuardianSetup /></SecuritySectionTabs>, "AAL1")}</Route>
    <Route path="/security-centre">{appShellPage(context, <SecuritySectionTabs>{context.user && <SecurityCentre user={context.user} />}</SecuritySectionTabs>, "AAL1")}</Route>
    <Route path="/security-keys">{appShellPage(context, <SecuritySectionTabs>{context.user && <SecurityKeys user={context.user} />}</SecuritySectionTabs>, "AAL1")}</Route>
    <Route path="/security-recovery">{appShellPage(context, <SecuritySectionTabs><SecurityRecovery /></SecuritySectionTabs>, "AAL1")}</Route>
    <Route path="/settings">{appShellPage(context, context.user && <SettingsPage user={context.user} />, "AAL1")}</Route>
    <Route path="/changelog">{appShellPage(context, <ChangelogPage />, "AAL1")}</Route>
    <Route path="/memories">{appShellPage(context, <SecuritySectionTabs><MemoryVault /></SecuritySectionTabs>, "AAL1")}</Route>
    <Route path="/alerts/:clusterId?">{appShellPage(context, <AlertsPage />)}</Route>
    <Route path="/dashboard/alerts"><Redirect to="/alerts" /></Route>
    <Route path="/dashboard/alerts/:clusterId">{(params) => <Redirect to={`/alerts/${params.clusterId}`} />}</Route>
    <Route path="/dashboard/:tab?">{(params) => {
      const redirect = context.resolveDashboardTabRedirect(params.tab, window.location.search);
      return redirect ? <Redirect to={redirect} /> : <DashboardRoute context={context} initialTab={context.validOrgTab(params.tab)} />;
    }}</Route>
    {/* Personal vault — AAL2, NOT AAL3 (owner-directed, 2026-08-10, ledger #75).
        The route-level AAL3 floor was not a control: no `/api/vault/identifiers*`
        handler mounts `requireAAL`, and the real per-ROW floor lives in the data
        (`vault_identifiers.minimum_aal`, filtered server-side in the list query)
        — which is AAL2 for five of the six categories. So the wall blocked the
        page while the API served those same rows to any signed-in session. The
        one category with a genuine AAL3 floor is government_id, and the page
        still gates that group itself (vault-identifiers/index.tsx). What
        protects a stored VALUE is the per-reveal fresh WebAuthn assertion its
        key is derived from — possession proven at the moment of reading, which
        a session-wide AAL flag cannot do. */}
    <Route path="/vault-identifiers">{appShellPage(context, <VaultIdentifiers currentAal={context.user?.aal ?? "AAL2"} />, "AAL2")}</Route>
    <Route path="/vault/identifiers/:id/history">
      <Guarded user={context.user} loading={context.loading} minAal="AAL2"><VaultIdentifierHistoryRedirect /></Guarded>
    </Route>
    <Route path="/vault-identifier-history/:id">
      <Guarded user={context.user} loading={context.loading} minAal="AAL2"><VaultIdentifierHistoryRedirect /></Guarded>
    </Route>
    <Route path="/auth-requests">{appShellPage(context, <AuthRequestListPage />)}</Route>
    <Route path="/auth-requests/:id">{appShellPage(context, <AuthRequestApprovalPage />)}</Route>
    <Route path="/guardian-stepup/:handshakeId">
      {(params) => <Guarded user={context.user} loading={context.loading} minAal="AAL2"><GuardianStepUpPage handshakeId={params.handshakeId} /></Guarded>}
    </Route>
    <Route path="/recovery-approval/:sessionId">
      {(params) => <Guarded user={context.user} loading={context.loading} minAal="AAL2"><RecoveryApprovalPage sessionId={params.sessionId} /></Guarded>}
    </Route>
    {/* Guardian Review — "prompted every 6 months at login" (Phase A Item 10b).
        AAL2 (default appShellPage floor): review/status + guardians + guardian-health
        are requireAuth-only, but complete/remove (server/routes/guardian-review.routes.ts)
        require requireAAL("AAL2"), so the page as a whole needs AAL2. */}
    <Route path="/guardian-review">{appShellPage(context, <GuardianReview />)}</Route>
    <Route path="/audit/groups">{appShellPage(context, <AuditGroupsPage />)}</Route>
    <Route path="/audit">{appShellPage(context, <AuditPage />)}</Route>
    <Route path="/org-vault">{appShellPage(context, <OrgVaultPage />)}</Route>
    <Route path="/org-vault/payouts/approve/:requestId">{appShellPage(context, <OrgVaultPayoutApprovePage />, "AAL3")}</Route>
    <Route path="/org-vault/payouts/request/:requestId">{appShellPage(context, <OrgVaultPayoutRequestPage />, "AAL3")}</Route>
    <Route path="/org-vault/payouts">{appShellPage(context, <OrgVaultPayoutsPage />, "AAL3")}</Route>
    <Route path="/connections">{appShellPage(context, <ConnectionsPage />)}</Route>
    <Route path="/invitations">{appShellPage(context, <InvitationsPage />)}</Route>
    <Route path="/deals/:dealId">{appShellPage(context, <DealDetailPage />)}</Route>
    <Route path="/deals">{appShellPage(context, <DealsPage />)}</Route>
  </>;
}
